import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { workoutPlannerAPI, equipmentAPI } from '../services/api'
import { trainingLocationsAPI } from '../services/trainingLocations'
import useTranslationCache from '../hooks/useTranslationCache'
import { resolveTemplateTargets, metricFieldLabel } from '../components/workouts/workoutHelpers'
import { formatDifficultyRange } from '../utils/templateDifficulty'
import ModuleLocationWarning from '../components/training/ModuleLocationWarning'
import ModuleVariantViewer from '../components/training/ModuleVariantViewer'
import {
  RectangleGroupIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  ClockIcon,
  MapPinIcon,
  XMarkIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlayCircleIcon,
  ChatBubbleLeftIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline'
import { contentLanguage } from '../i18n/supported'

const TEMPLATE_TYPES = [
  { value: 'strength', color: '#3B82F6', icon: 'weight' },
  { value: 'cardio', color: '#EF4444', icon: 'heart' },
  { value: 'endurance_strength', color: '#F59E0B', icon: 'bolt' },
  { value: 'mix', color: '#8B5CF6', icon: 'mix' },
  { value: 'hiit', color: '#EC4899', icon: 'fire' },
  { value: 'recovery', color: '#10B981', icon: 'leaf' },
  { value: 'custom', color: '#6B7280', icon: 'custom' }
]

// Must match backend MuscleGroupEnum: chest, back, arms, shoulders, legs, core, full_body
const MUSCLE_GROUPS = ['chest', 'back', 'legs', 'shoulders', 'arms', 'core', 'full_body']

const DIFFICULTY_LEVELS = [
  { value: 'beginner', color: 'green' },
  { value: 'intermediate', color: 'yellow' },
  { value: 'advanced', color: 'red' },
]

const DEFAULT_COLORS = [
  '#3B82F6', '#EF4444', '#F59E0B', '#10B981', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'
]

export default function TrainingTemplates() {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedType, setSelectedType] = useState('')
  // VH-134. Empty means EVERY module, including those with no place — a filter
  // that defaults to one location is how VH-130's equipment list came to look
  // like data loss.
  const [selectedLocation, setSelectedLocation] = useState('')
  // VH-134: "Build a module here" arrives as ?location= from the coverage
  // panel, so the new module opens already tied to that place.
  const [searchParams] = useSearchParams()
  const buildForLocationId = searchParams.get('location') || ''
  const [selectedMuscleGroup, setSelectedMuscleGroup] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [expandedTemplateId, setExpandedTemplateId] = useState(null)
  const [showExerciseModal, setShowExerciseModal] = useState(false)
  const [selectedTemplateForExercise, setSelectedTemplateForExercise] = useState(null)

  // Get current language for translation handling
  const currentLanguage = contentLanguage(i18n?.language)

  // Fetch templates with exercises
  const { data: templatesData, isLoading } = useQuery({
    queryKey: ['trainingTemplates', searchTerm, selectedType, selectedLocation],
    queryFn: () => workoutPlannerAPI.getTemplates({
      search_term: searchTerm || undefined,
      template_type: selectedType || undefined,
      location_id: selectedLocation && selectedLocation !== 'none' ? selectedLocation : undefined,
      without_location: selectedLocation === 'none' ? true : undefined
    })
  })

  // VH-134: places, for the module's optional location picker and the filter.
  const { data: locationsData } = useQuery({
    queryKey: ['trainingLocations', 'forModules'],
    queryFn: () => trainingLocationsAPI.list().then((r) => r.data),
  })

  // Fetch exercise library for adding exercises (use 'limit' parameter, not 'page_size')
  const { data: exercisesData } = useQuery({
    queryKey: ['exercises'],
    queryFn: () => workoutPlannerAPI.getExercises({ limit: 100 })
  })

  // Fetch equipment for filters (use limit: 100 to get all equipment, sorted alphabetically)
  const { data: equipmentData } = useQuery({
    queryKey: ['equipment'],
    queryFn: () => equipmentAPI.getAll({ limit: 100, sort_by: 'name_en' })
  })

  const allTemplates = templatesData?.data?.templates || []
  const exercises = exercisesData?.data?.exercises || []
  const equipment = equipmentData?.data?.equipment || []

  // Client-side filter by muscle group (case-insensitive)
  const templates = selectedMuscleGroup
    ? allTemplates.filter(t =>
        t.target_muscle_groups?.some(g => g.toLowerCase() === selectedMuscleGroup.toLowerCase())
      )
    : allTemplates

  // Fetch template details when expanded
  const { data: expandedTemplateData } = useQuery({
    queryKey: ['templateDetail', expandedTemplateId],
    queryFn: () => workoutPlannerAPI.getTemplate(expandedTemplateId),
    enabled: !!expandedTemplateId
  })

  // VH-36: Translation caches via shared hook
  const { getTranslation: getTemplateTranslation } = useTranslationCache({
    items: allTemplates,
    prefix: 'template',
    fetchTranslation: (id, lang) => workoutPlannerAPI.translateTemplate(id, lang),
  })
  const { getTranslation: getExerciseTranslation } = useTranslationCache({
    items: exercises,
    prefix: 'exercise',
    fetchTranslation: (id, lang) => workoutPlannerAPI.translateExercise(id, lang),
  })
  const { getTranslation: getEquipmentTranslation } = useTranslationCache({
    items: equipment,
    prefix: 'equipment',
    fetchTranslation: (id, lang) => equipmentAPI.translate(id, lang),
  })

  // Helper to get template name (translated or English)
  const getTemplateName = useCallback((template) => {
    if (!template) return ''
    return getTemplateTranslation(template.id, 'name') || template.name_en
  }, [getTemplateTranslation])

  // Helper to get template description (translated or English)
  const getTemplateDescription = useCallback((template) => {
    if (!template) return ''
    return getTemplateTranslation(template.id, 'description') || template.description_en || ''
  }, [getTemplateTranslation])

  // Helper to get exercise name (translated or English)
  const getExerciseName = useCallback((exercise) => {
    if (!exercise) return ''
    return getExerciseTranslation(exercise.id, 'name') || exercise.name_en || exercise.name || ''
  }, [getExerciseTranslation])

  // Helper to get equipment name (translated or English)
  const getEquipmentName = useCallback((eq) => {
    if (!eq) return ''
    return getEquipmentTranslation(eq.id, 'name') || eq.name_en || eq.name || ''
  }, [getEquipmentTranslation])

  // Create mutation
  const createMutation = useMutation({
    mutationFn: workoutPlannerAPI.createTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingTemplates'] })
      setShowModal(false)
      setEditingTemplate(null)
    }
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => workoutPlannerAPI.updateTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingTemplates'] })
      queryClient.invalidateQueries({ queryKey: ['templateDetail'] })
      setShowModal(false)
      setEditingTemplate(null)
    }
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: workoutPlannerAPI.deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingTemplates'] })
    }
  })

  // Add exercise to template mutation
  const addExerciseMutation = useMutation({
    mutationFn: ({ templateId, data }) => workoutPlannerAPI.addExerciseToTemplate(templateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templateDetail'] })
      queryClient.invalidateQueries({ queryKey: ['trainingTemplates'] })
    }
  })

  // Update exercise in template mutation
  const updateExerciseMutation = useMutation({
    mutationFn: ({ templateId, exerciseId, data }) =>
      workoutPlannerAPI.updateTemplateExercise(templateId, exerciseId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templateDetail'] })
    }
  })

  // Remove exercise from template mutation
  const removeExerciseMutation = useMutation({
    mutationFn: ({ templateId, exerciseId }) =>
      workoutPlannerAPI.removeExerciseFromTemplate(templateId, exerciseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templateDetail'] })
      queryClient.invalidateQueries({ queryKey: ['trainingTemplates'] })
    }
  })

  // Reorder exercises in template mutation
  const reorderMutation = useMutation({
    mutationFn: ({ templateId, exerciseIds }) =>
      workoutPlannerAPI.reorderTemplateExercises(templateId, exerciseIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templateDetail'] })
    }
  })

  const handleMoveExercise = (templateId, exerciseList, exerciseId, direction) => {
    const idx = exerciseList.findIndex(e => e.id === exerciseId)
    const newIdx = idx + direction
    if (newIdx < 0 || newIdx >= exerciseList.length) return

    const reordered = [...exerciseList]
    ;[reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]]
    reorderMutation.mutate({ templateId, exerciseIds: reordered.map(e => e.id) })
  }

  const handleDelete = (id) => {
    if (confirm(t('templates.confirmDelete'))) {
      deleteMutation.mutate(id)
    }
  }

  const handleEdit = (template) => {
    setEditingTemplate(template)
    setShowModal(true)
  }

  const handleAdd = () => {
    setEditingTemplate(null)
    setShowModal(true)
  }

  const handleSave = (formData) => {
    // VH-134: the select yields a string, and an empty one means "no place".
    // On update that has to be sent as 0 rather than omitted — a partial update
    // cannot tell "leave the location alone" from "clear it", so 0 is the
    // explicit clear the API documents.
    const payload = {
      ...formData,
      location_id: formData.location_id
        ? Number(formData.location_id)
        : editingTemplate
          ? 0
          : undefined,
    }
    if (editingTemplate) {
      updateMutation.mutate({ id: editingTemplate.id, data: payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  const handleToggleExpand = (templateId) => {
    setExpandedTemplateId(expandedTemplateId === templateId ? null : templateId)
  }

  const handleAddExercise = (template) => {
    setSelectedTemplateForExercise(template)
    setShowExerciseModal(true)
  }

  const handleExerciseSelect = (exerciseId, notes = '') => {
    if (selectedTemplateForExercise) {
      const templateExercises = expandedTemplateData?.data?.exercises || []
      // Find the exercise definition to get its default values
      const exerciseDef = exercises.find(e => e.id === exerciseId)
      addExerciseMutation.mutate({
        templateId: selectedTemplateForExercise.id,
        data: {
          exercise_definition_id: exerciseId,
          order_in_template: templateExercises.length,
          notes: notes,
          training_sets: exerciseDef?.default_training_sets || 3,
          warmup_sets: exerciseDef?.default_warmup_sets || 0,
          rest_seconds: exerciseDef?.default_rest_seconds || 60
        }
      })
    }
    setShowExerciseModal(false)
    setSelectedTemplateForExercise(null)
  }

  const handleRemoveExercise = (templateId, exerciseId) => {
    if (confirm(t('templates.confirmRemoveExercise'))) {
      removeExerciseMutation.mutate({ templateId, exerciseId })
    }
  }

  const handleUpdateExerciseNotes = (templateId, exerciseId, notes) => {
    updateExerciseMutation.mutate({
      templateId,
      exerciseId,
      data: { notes }
    })
  }

  const getTypeColor = (type) => {
    const templateType = TEMPLATE_TYPES.find(t => t.value === type)
    return templateType?.color || '#6B7280'
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('templates.title')}</h1>
            <p className="mt-2 text-gray-600">{t('templates.subtitle')}</p>
          </div>
          <button onClick={handleAdd} className="btn-primary flex items-center">
            <PlusIcon className="h-5 w-5 mr-2" />
            {t('templates.create')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 space-y-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder={t('templates.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        {/* VH-134: filter modules by the place they are for. Defaults to ALL,
            including modules with no location — a filter that silently defaults
            to one place is how VH-130's equipment list came to look like data
            loss. */}
        <div className="flex items-center space-x-4">
          <label
            className="text-sm font-medium text-gray-700"
            htmlFor="module-location-filter"
          >
            {t('trainingLocations.module.filterByLocation')}:
          </label>
          <select
            id="module-location-filter"
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            className="text-sm focus:border-primary-500 focus:ring-primary-500"
            data-testid="module-location-filter"
          >
            <option value="">{t('trainingLocations.module.allLocations')}</option>
            {/* VH-137: finding the modules that still need a place is a reason
                to open this list, so "none" is a value rather than the absence
                of a filter. Sent as `without_location`, not `location_id=0` —
                0 is already VH-134's "clear it" sentinel on update. */}
            <option value="none">{t('trainingLocations.module.noLocation')}</option>
            {(locationsData?.locations || []).map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </select>
        </div>

        {/* Type Filter */}
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">{t('templates.filterByType')}:</span>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedType('')}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                selectedType === ''
                  ? 'bg-gray-900 text-on-neutral'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('templates.allTypes')}
            </button>
            {TEMPLATE_TYPES.map((type) => (
              <button
                key={type.value}
                onClick={() => setSelectedType(type.value)}
                style={{
                  backgroundColor: selectedType === type.value ? type.color : undefined,
                  color: selectedType === type.value ? 'white' : undefined
                }}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  selectedType === type.value ? '' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t(`templates.types.${type.value}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Muscle Group Filter */}
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">{t('templates.filterByMuscle')}:</span>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedMuscleGroup('')}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                selectedMuscleGroup === ''
                  ? 'bg-gray-900 text-on-neutral'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('common.all')}
            </button>
            {MUSCLE_GROUPS.map((group) => (
              <button
                key={group}
                onClick={() => setSelectedMuscleGroup(group)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  selectedMuscleGroup === group
                    ? 'bg-primary-600 text-on-primary'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t(`exercises.muscleGroups.${group}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Templates List */}
      {templates.length > 0 ? (
        <div className="space-y-4">
          {templates.map((template) => {
            const isExpanded = expandedTemplateId === template.id
            const templateDetail = isExpanded ? expandedTemplateData?.data : null
            const templateExercises = templateDetail?.exercises || []

            return (
              <div
                key={template.id}
                className={`card ${isExpanded ? 'ring-2 ring-primary-500' : ''}`}
              >
                {/* Card Header */}
                <div
                  className="card-header flex items-center justify-between cursor-pointer"
                  onClick={() => handleToggleExpand(template.id)}
                >
                  {/* min-w-0 + flex-1 let the title and meta row WRAP inside the
                      card. Without them a module's gym label (VH-134) widened
                      this block past the card on a phone and pushed the expand
                      chevron out of sight (UAT 2026-09-14). */}
                  <div className="flex items-center min-w-0 flex-1">
                    <div
                      className="h-10 w-10 flex-shrink-0 rounded-lg flex items-center justify-center mr-3"
                      style={{ backgroundColor: template.color || getTypeColor(template.template_type) }}
                    >
                      <RectangleGroupIcon className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 break-words">
                        {getTemplateName(template)}
                      </h3>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-gray-500">
                        <span
                          className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: `${getTypeColor(template.template_type)}20`,
                            color: getTypeColor(template.template_type)
                          }}
                        >
                          {t(`templates.types.${template.template_type}`)}
                        </span>
                        <span className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {template.estimated_duration_minutes} {t('common.minutes')}
                        </span>
                        {/* VH-134: the place this module is for. Only shown when
                            it has one — an outdoor module has no gap to explain. */}
                        {template.location_name && (
                          <span
                            className="flex items-center"
                            data-testid={`module-location-${template.id}`}
                          >
                            <MapPinIcon className="h-4 w-4 mr-1" />
                            {template.location_name}
                          </span>
                        )}
                      </div>
                      <ModuleLocationWarning template={template} />
                    </div>
                  </div>

                  <div className="flex flex-shrink-0 items-center space-x-2 sm:space-x-4 ml-2">
                    {/* Edit/Delete buttons */}
                    <div className="flex space-x-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleEdit(template)}
                        className="p-2 rounded hover:bg-gray-100"
                        title={t('common.edit')}
                      >
                        <PencilIcon className="h-4 w-4 text-gray-400" />
                      </button>
                      <button
                        onClick={() => handleDelete(template.id)}
                        className="p-2 rounded hover:bg-gray-100"
                        title={t('common.delete')}
                      >
                        <TrashIcon className="h-4 w-4 text-gray-400" />
                      </button>
                    </div>
                    {isExpanded ? (
                      <ChevronUpIcon className="h-5 w-5 text-gray-400" data-testid={`module-chevron-${template.id}`} />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5 text-gray-400" data-testid={`module-chevron-${template.id}`} />
                    )}
                  </div>
                </div>

                {/* Card Body - Expanded Content */}
                {isExpanded && (
                  <div className="card-body pt-0">
                    {/* Description */}
                    {getTemplateDescription(template) && (
                      <p className="text-sm text-gray-600 mb-4">
                        {getTemplateDescription(template)}
                      </p>
                    )}

                    {/* VH-125: difficulty is owned by the exercise, so the
                        template only ever summarises it — read-only, no edit
                        control. Collapses to one label when the exercises agree. */}
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-500 mb-1">
                        {t('templates.difficulty', 'Difficulty')}
                        <span className="ml-1 font-normal text-gray-400">
                          {t('templates.fromExercises', 'from the exercises in this template')}
                        </span>
                      </p>
                      <p className="text-sm text-gray-900">
                        {formatDifficultyRange(template, t)}
                      </p>
                    </div>

                    {/* VH-125: the curated focus is the author's intent and stays
                        editable; the derived coverage below it is what the
                        exercises actually train. Showing both makes a
                        disagreement visible instead of letting one contradict
                        the other silently. */}
                    {template.target_muscle_groups && template.target_muscle_groups.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-gray-500 mb-1">
                          {t('templates.focusCurated', 'Focus')}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {template.target_muscle_groups.map((group) => (
                            <span
                              key={group}
                              className="inline-block px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600"
                            >
                              {t(`exercises.muscleGroups.${group}`)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-500 mb-1">
                        {t('templates.coveredByExercises', 'Covered by exercises')}
                      </p>
                      {template.derived_muscle_groups?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {template.derived_muscle_groups.map((group) => (
                            <span
                              key={group}
                              className="inline-block px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700"
                            >
                              {t(`exercises.muscleGroups.${group}`)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400">
                          {t('templates.noExercisesYet', 'No exercises yet')}
                        </p>
                      )}

                      {template.uncovered_focus_groups?.length > 0 && (
                        <p className="mt-2 text-xs text-amber-700">
                          {t('templates.focusNotCovered', {
                            groups: template.uncovered_focus_groups
                              .map((g) => t(`exercises.muscleGroups.${g}`))
                              .join(', '),
                            defaultValue:
                              '{{groups}} is in the focus but no exercise in this template covers it.',
                          })}
                        </p>
                      )}
                    </div>

                    {/* Exercises Table */}
                    {templateExercises.length > 0 ? (
                      <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
                        {/* Table Header */}
                        <div className="grid grid-cols-12 gap-2 items-center py-2 px-3 bg-gray-100 text-xs font-medium text-gray-600 border-b border-gray-200">
                          <div className="col-span-2">{t('exercises.muscle', { defaultValue: 'Muscle' })}</div>
                          <div className="col-span-3">{t('trainingDays.exercise', { defaultValue: 'Exercise' })}</div>
                          <div className="col-span-2 text-center">{t('templates.targets', { defaultValue: 'Targets' })}</div>
                          <div className="col-span-3">{t('templates.form.exerciseComments')}</div>
                          <div className="col-span-2 text-center">{t('common.actions')}</div>
                        </div>

                        {/* Exercise Rows */}
                        {templateExercises.map((exercise, index) => (
                          <ExerciseTableRow
                            key={exercise.id}
                            exercise={exercise}
                            templateId={template.id}
                            getExerciseName={getExerciseName}
                            onRemove={handleRemoveExercise}
                            onUpdateNotes={handleUpdateExerciseNotes}
                            onMove={handleMoveExercise}
                            exercises={templateExercises}
                            index={index}
                            t={t}
                            currentLanguage={currentLanguage}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-lg mb-4">
                        <p className="text-sm text-gray-500">{t('templates.noExercises')}</p>
                      </div>
                    )}

                    {/* VH-207: per-location numbers — a viewer, never a control. */}
                    <ModuleVariantViewer templateId={template.id} />

                    {/* Add Exercise Button */}
                    <button
                      onClick={() => handleAddExercise(template)}
                      className="btn-outline btn-sm flex items-center"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      {t('templates.addExercise')}
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <RectangleGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('templates.noTemplates')}</h3>
          <p className="mt-1 text-sm text-gray-500">{t('templates.getStarted')}</p>
          <div className="mt-6">
            <button onClick={handleAdd} className="btn-primary">
              <PlusIcon className="h-5 w-5 mr-2 inline" />
              {t('templates.create')}
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Module Modal */}
      {showModal && (
        <TemplateModal
          template={editingTemplate}
          onClose={() => {
            setShowModal(false)
            setEditingTemplate(null)
          }}
          onSave={handleSave}
          isSaving={createMutation.isPending || updateMutation.isPending}
          locations={locationsData?.locations || []}
          defaultLocationId={buildForLocationId}
        />
      )}

      {/* Add Exercise Modal */}
      {showExerciseModal && (
        <AddExerciseModal
          exercises={exercises}
          equipment={equipment}
          getExerciseName={getExerciseName}
          getEquipmentName={getEquipmentName}
          onClose={() => {
            setShowExerciseModal(false)
            setSelectedTemplateForExercise(null)
          }}
          onSelect={handleExerciseSelect}
          t={t}
        />
      )}
    </div>
  )
}

/**
 * Exercise Table Row - Excel-style display
 */
function ExerciseTableRow({ exercise, templateId, getExerciseName, onRemove, onUpdateNotes, onMove, exercises, index, t, currentLanguage }) {
  const [isEditingNotes, setIsEditingNotes] = useState(false)
  const [notes, setNotes] = useState(exercise.notes || '')

  const handleSaveNotes = () => {
    onUpdateNotes(templateId, exercise.id, notes)
    setIsEditingNotes(false)
  }

  const exerciseDef = exercise.exercise_definition
  // VH-144: what this exercise actually measures. An exercise configured with
  // its own metrics (a hike's duration / km / intensity) shows those; a
  // set-based one shows "warmup / sets". Previously both collapsed to a dash
  // for anything non-strength, hiding configuration the user had entered.
  const targets = resolveTemplateTargets(exerciseDef, exercise)

  return (
    <div className="grid grid-cols-12 gap-2 items-center py-2 px-3 text-sm border-b border-gray-200 last:border-b-0 hover:bg-gray-50">
      {/* Muscle Group */}
      <div className="col-span-2 text-xs text-gray-600 font-medium">
        {exerciseDef?.muscle_group
          ? t(`exercises.muscleGroups.${exerciseDef.muscle_group}`)
          : '-'}
      </div>

      {/* Exercise Name with Video */}
      <div className="col-span-3 flex items-center">
        <span className="font-medium text-gray-900 truncate">
          {exerciseDef ? getExerciseName(exerciseDef) : `Exercise #${exercise.exercise_definition_id}`}
        </span>
        {exerciseDef?.effective_video_url && (
          <a
            href={exerciseDef.effective_video_url}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-2 text-primary-600 hover:text-primary-700 flex-shrink-0"
            title={t('exercises.modal.watchVideoTutorial')}
            onClick={(e) => e.stopPropagation()}
          >
            <PlayCircleIcon className="h-4 w-4" />
          </a>
        )}
      </div>

      {/* Targets — sets for set-based exercises, configured metrics otherwise */}
      <div className="col-span-2 text-xs text-gray-600 text-center">
        {targets.kind === 'sets' && (
          <span title={t('templates.warmupAndSets', { defaultValue: 'Warmup / Sets' })}>
            {targets.warmupSets} / {targets.trainingSets}
          </span>
        )}
        {targets.kind === 'metrics' && (
          <span className="inline-flex flex-wrap justify-center gap-x-1.5 gap-y-0.5">
            {targets.fields.map((field, i) => (
              <span key={field.key} className="whitespace-nowrap">
                {i > 0 && <span className="text-gray-400 mr-1.5">·</span>}
                {metricFieldLabel(field, currentLanguage)}
              </span>
            ))}
          </span>
        )}
        {targets.kind === 'none' && '—'}
      </div>

      {/* Comments */}
      <div className="col-span-3">
        {isEditingNotes ? (
          <div className="flex gap-1">
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('templates.commentsPlaceholder')}
              className="flex-1 text-xs focus:border-primary-500 focus:ring-primary-500 py-1 px-2"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveNotes()
                if (e.key === 'Escape') {
                  setNotes(exercise.notes || '')
                  setIsEditingNotes(false)
                }
              }}
            />
            <button
              onClick={handleSaveNotes}
              className="p-1 text-green-600 hover:bg-green-50 rounded"
            >
              <CheckIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => {
                setNotes(exercise.notes || '')
                setIsEditingNotes(false)
              }}
              className="p-1 text-gray-400 hover:bg-gray-100 rounded"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div
            className="flex items-center text-xs text-gray-500 cursor-pointer hover:text-gray-700"
            onClick={() => setIsEditingNotes(true)}
          >
            <ChatBubbleLeftIcon className="h-3 w-3 mr-1 flex-shrink-0" />
            {exercise.notes ? (
              <span className="truncate">{exercise.notes}</span>
            ) : (
              <span className="italic">{t('templates.addComment')}</span>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="col-span-2 flex justify-center gap-0.5">
        <button
          onClick={() => onMove(templateId, exercises, exercise.id, -1)}
          disabled={index === 0}
          className="p-1 text-gray-400 hover:text-primary-500 hover:bg-primary-50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
          title={t('common.moveUp', { defaultValue: 'Move up' })}
        >
          <ArrowUpIcon className="h-4 w-4" />
        </button>
        <button
          onClick={() => onMove(templateId, exercises, exercise.id, 1)}
          disabled={index === exercises.length - 1}
          className="p-1 text-gray-400 hover:text-primary-500 hover:bg-primary-50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
          title={t('common.moveDown', { defaultValue: 'Move down' })}
        >
          <ArrowDownIcon className="h-4 w-4" />
        </button>
        <button
          onClick={() => onRemove(templateId, exercise.id)}
          className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
          title={t('common.remove')}
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

/**
 * Add Exercise Modal - Uses dropdown filters matching Exercise Library
 */
function AddExerciseModal({ exercises, equipment, getExerciseName, getEquipmentName, onClose, onSelect, t }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedMuscle, setSelectedMuscle] = useState('')
  const [selectedDifficulty, setSelectedDifficulty] = useState('')
  const [selectedEquipment, setSelectedEquipment] = useState('')
  const [selectedExercise, setSelectedExercise] = useState(null)
  const [notes, setNotes] = useState('')

  const filteredExercises = exercises.filter(ex => {
    const matchesSearch = getExerciseName(ex).toLowerCase().includes(searchTerm.toLowerCase())
    // Normalize muscle_group comparison - handle both "BACK" and "back" formats
    const exMuscle = String(ex.muscle_group || '').toLowerCase()
    const matchesMuscle = !selectedMuscle || exMuscle === selectedMuscle.toLowerCase()
    // Normalize difficulty comparison
    const exDifficulty = String(ex.difficulty_level || '').toLowerCase()
    const matchesDifficulty = !selectedDifficulty || exDifficulty === selectedDifficulty.toLowerCase()
    const matchesEquipment = !selectedEquipment || ex.equipment_id === parseInt(selectedEquipment)
    return matchesSearch && matchesMuscle && matchesDifficulty && matchesEquipment
  })

  const handleConfirm = () => {
    if (selectedExercise) {
      onSelect(selectedExercise.id, notes)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="relative bg-surface-container rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold">{t('templates.selectExercise')}</h3>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Search & Filters - Modern pill style */}
          <div className="p-4 border-b space-y-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('exercises.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full bg-gray-50 focus:border-primary-500 focus:ring-primary-500 focus:bg-surface-container transition-colors"
              />
            </div>

            {/* Muscle Group Pills */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">{t('exercises.muscle')}</label>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setSelectedMuscle('')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    selectedMuscle === ''
                      ? 'bg-primary-600 text-on-primary shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t('common.all')}
                </button>
                {MUSCLE_GROUPS.map((group) => (
                  <button
                    key={group}
                    onClick={() => setSelectedMuscle(group)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      selectedMuscle === group
                        ? 'bg-primary-600 text-on-primary shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {t(`exercises.muscleGroups.${group}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Difficulty & Equipment Row */}
            <div className="flex gap-4">
              {/* Difficulty Pills */}
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-500 mb-2">{t('exercises.difficulty.label')}</label>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    onClick={() => setSelectedDifficulty('')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      selectedDifficulty === ''
                        ? 'bg-gray-700 text-on-neutral shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {t('common.all')}
                  </button>
                  {DIFFICULTY_LEVELS.map((level) => (
                    <button
                      key={level.value}
                      onClick={() => setSelectedDifficulty(level.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        selectedDifficulty === level.value
                          ? level.color === 'green' ? 'bg-green-500 text-on-neutral shadow-sm'
                          : level.color === 'yellow' ? 'bg-amber-500 text-on-neutral shadow-sm'
                          : 'bg-red-500 text-on-neutral shadow-sm'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {t(`exercises.difficulty.${level.value}`)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Equipment Dropdown - styled nicely */}
              {equipment.length > 0 && (
                <div className="w-48">
                  <label className="block text-xs font-medium text-gray-500 mb-2">{t('nav.equipment')}</label>
                  <div className="relative">
                    <select
                      value={selectedEquipment}
                      onChange={(e) => setSelectedEquipment(e.target.value)}
                      className="w-full bg-gray-100 py-1.5 pl-4 pr-8 text-xs font-medium text-gray-600 hover:bg-gray-200 focus:bg-surface-container focus:ring-2 focus:ring-primary-500 transition-all cursor-pointer"
                    >
                      <option value="">{t('common.all')}</option>
                      {equipment.map((eq) => (
                        <option key={eq.id} value={eq.id}>
                          {getEquipmentName(eq)}
                        </option>
                      ))}
                    </select>
                    <ChevronDownIcon className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Exercise List */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Filter status */}
            <div className="text-xs text-gray-400 mb-2">
              {filteredExercises.length} / {exercises.length} {t('exercises.title').toLowerCase()}
              {selectedMuscle && ` • ${t(`exercises.muscleGroups.${selectedMuscle}`)}`}
              {selectedDifficulty && ` • ${t(`exercises.difficulty.${selectedDifficulty}`)}`}
            </div>
            <div className="space-y-2">
              {filteredExercises.length > 0 ? (
                filteredExercises.map((exercise) => (
                  <div
                    key={exercise.id}
                    onClick={() => setSelectedExercise(exercise)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedExercise?.id === exercise.id
                        ? 'bg-primary-50 border-2 border-primary-500'
                        : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                    }`}
                  >
                    <div className="font-medium text-gray-900">{getExerciseName(exercise)}</div>
                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                      <span>{t(`exercises.muscleGroups.${exercise.muscle_group}`)}</span>
                      <span className="text-gray-300">|</span>
                      <span>{t(`exercises.difficulty.${exercise.difficulty_level?.toLowerCase()}`)}</span>
                      {exercise.equipment && (
                        <>
                          <span className="text-gray-300">|</span>
                          <span>{getEquipmentName(exercise.equipment)}</span>
                        </>
                      )}
                      {exercise.effective_video_url && (
                        <span className="flex items-center text-primary-600 ml-auto">
                          <PlayCircleIcon className="h-3 w-3 mr-0.5" />
                          Video
                        </span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {t('exercises.noExercisesFound')}
                </div>
              )}
            </div>
          </div>

          {/* Notes Input & Actions */}
          {selectedExercise && (
            <div className="p-4 border-t space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('templates.form.exerciseComments')}
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('templates.commentsPlaceholder')}
                  className="w-full focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={onClose} className="btn-secondary">
                  {t('common.cancel')}
                </button>
                <button onClick={handleConfirm} className="btn-primary">
                  {t('templates.addExercise')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Template Modal (create/edit module metadata)
 * Styled to match EquipmentFormModal
 */
function TemplateModal({ template, onClose, onSave, isSaving, defaultLocationId, locations = [] }) {
  const { t, i18n } = useTranslation()
  const currentLanguage = contentLanguage(i18n?.language)
  const [languageWarning, setLanguageWarning] = useState(null)

  const [formData, setFormData] = useState({
    name_de: template?.name_en || template?.name_de || '',
    name_en: template?.name_en || '',
    template_type: template?.template_type || 'strength',
    target_muscle_groups: template?.target_muscle_groups || [],
    description_de: template?.description_en || template?.description_de || '',
    description_en: template?.description_en || '',
    estimated_duration_minutes: template?.estimated_duration_minutes || 60,
    color: template?.color || DEFAULT_COLORS[0],
    // VH-134: the place this module is for. Optional forever — outdoor modules
    // like SET 3 legitimately have no location, and forcing one would make the
    // field a lie. `defaultLocationId` is set when the module is being built
    // from a location's coverage panel.
    location_id: template?.location_id || defaultLocationId || ''
  })

  useEffect(() => {
    if (currentLanguage !== 'en') {
      const languageNames = {
        de: 'German',
        'pt-BR': 'Portuguese',
        fr: 'French',
        es: 'Spanish'
      }
      setLanguageWarning(languageNames[currentLanguage] || currentLanguage)
    }
  }, [currentLanguage])

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === 'name') {
      setFormData(prev => ({ ...prev, name_en: value, name_de: value }))
    } else if (name === 'description') {
      setFormData(prev => ({ ...prev, description_en: value, description_de: value }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave(formData)
  }

  const toggleMuscleGroup = (group) => {
    setFormData(prev => ({
      ...prev,
      target_muscle_groups: prev.target_muscle_groups.includes(group)
        ? prev.target_muscle_groups.filter(g => g !== group)
        : [...prev.target_muscle_groups, group]
    }))
  }

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-surface-container rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {template ? t('templates.edit') : t('templates.create')}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Language Warning Banner */}
          {languageWarning && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-amber-800">
                    {t('templates.form.languageWarningTitle', { language: languageWarning })}
                  </h3>
                  <p className="mt-1 text-sm text-amber-700">
                    {t('templates.form.languageWarningMessage')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Name field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('templates.form.name')} *
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name_en}
              onChange={handleChange}
              placeholder={t('templates.form.nameHint')}
              className="w-full focus:border-primary-500 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">{t('templates.form.nameHint')}</p>
          </div>

          {/* Two-column row: Type and Duration */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('templates.type')}
              </label>
              <select
                name="template_type"
                value={formData.template_type}
                onChange={handleChange}
                className="w-full focus:border-primary-500 focus:ring-primary-500"
              >
                {TEMPLATE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {t(`templates.types.${type.value}`)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('templates.estimatedDuration')} ({t('common.minutes')})
              </label>
              <input
                type="number"
                name="estimated_duration_minutes"
                min="15"
                max="180"
                value={formData.estimated_duration_minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, estimated_duration_minutes: parseInt(e.target.value) || 60 }))}
                className="w-full focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* VH-134: the place this module is for. Optional forever — outdoor
              modules like SET 3 legitimately have no location, so the blank
              option is a real choice rather than an unset field. */}
          <div>
            <label
              className="block text-sm font-medium text-gray-700 mb-1"
              htmlFor="module-location"
            >
              {t('trainingLocations.module.location')}
            </label>
            <select
              id="module-location"
              name="location_id"
              value={formData.location_id || ''}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, location_id: e.target.value }))
              }
              className="w-full focus:border-primary-500 focus:ring-primary-500"
              data-testid="module-location-select"
            >
              <option value="">{t('trainingLocations.module.noLocation')}</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('templates.form.description')}
            </label>
            <textarea
              name="description"
              value={formData.description_en}
              onChange={handleChange}
              rows={2}
              placeholder={t('templates.form.descriptionHint')}
              className="w-full focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          {/* Muscle Groups - Grid layout like Equipment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('templates.muscleGroups')}
            </label>
            <div className="grid grid-cols-3 gap-2 border border-gray-200 rounded-md p-3">
              {MUSCLE_GROUPS.map((group) => (
                <label key={group} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.target_muscle_groups.includes(group)}
                    onChange={() => toggleMuscleGroup(group)}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">
                    {t(`exercises.muscleGroups.${group}`)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Color picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('templates.color')}
            </label>
            <div className="flex space-x-3">
              {DEFAULT_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, color }))}
                  className={`w-8 h-8 rounded-full transition-transform ${
                    formData.color === color ? 'ring-2 ring-offset-2 ring-primary-500 scale-110' : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          {/* Footer buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-outline"
              disabled={isSaving}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSaving}
            >
              {isSaving ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
